import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, Target, TrendingUp, Cpu, ShieldOff, Users, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

/* ---------- Small primitives ---------- */
const Eyebrow = ({ children, color = "#FF3B30" }) => (
  <div className="font-mono text-[11px] uppercase tracking-[0.25em] mb-4" style={{ color }}>
    {children}
  </div>
);

const H1 = ({ children }) => (
  <h1 className="font-display font-black text-[clamp(2.5rem,7vw,5.5rem)] leading-[0.95] tracking-tight text-white">
    {children}
  </h1>
);

const H2 = ({ children }) => (
  <h2 className="font-display font-black text-[clamp(2rem,5vw,3.75rem)] leading-[1.02] tracking-tight text-white">
    {children}
  </h2>
);

/* ---------- Hero background — animated network nodes ---------- */
function NetworkCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, w, h;
    const nodes = [];
    const NODE_COUNT = 55;
    const resize = () => {
      w = canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      h = canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    };
    resize();
    window.addEventListener("resize", resize);
    for (let i = 0; i < NODE_COUNT; i++) {
      nodes.push({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.4 * window.devicePixelRatio,
        vy: (Math.random() - 0.5) * 0.4 * window.devicePixelRatio,
        r: (Math.random() * 1.5 + 0.5) * window.devicePixelRatio,
      });
    }
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      // links
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          const max = 160 * window.devicePixelRatio;
          if (d < max) {
            const alpha = (1 - d / max) * 0.18;
            ctx.strokeStyle = `rgba(255, 59, 48, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      // dots
      for (const n of nodes) {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
        ctx.fillStyle = "rgba(255,255,255,0.55)";
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return (
    <canvas
      ref={ref}
      className="absolute inset-0 w-full h-full opacity-70 pointer-events-none"
      data-testid="hero-network-canvas"
    />
  );
}

/* ---------- Consensus Engineering pillar ---------- */
function Pillar({ num, kicker, title, body, bullets, color = "#FF3B30" }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0c] p-6 sm:p-7 flex flex-col min-w-0 hover:border-white/25 transition-colors group">
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] mb-4" style={{ color }}>
        {num} — {kicker}
      </div>
      <h3 className="font-display text-xl sm:text-2xl font-bold text-white mb-3 tracking-tight">{title}</h3>
      <p className="text-[14px] leading-relaxed text-zinc-400 mb-5">{body}</p>
      <ul className="space-y-2 mt-auto">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-[13px] text-zinc-500 pb-2 border-b border-white/5 last:border-0">
            <ArrowRight className="w-3 h-3 mt-1 shrink-0" style={{ color }} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- Offering card ---------- */
function OfferingCard({ icon: Icon, title, body, num }) {
  return (
    <div className="border border-white/10 p-6 sm:p-8 bg-[#0a0a0c] min-w-0 relative overflow-hidden group hover:border-[#FF3B30]/50 transition-colors">
      <div className="absolute top-4 right-4 font-mono text-[10px] tracking-[0.2em] text-zinc-700">{num}</div>
      <div className="w-11 h-11 border border-[#FF3B30]/40 flex items-center justify-center mb-6 text-[#FF3B30] group-hover:bg-[#FF3B30]/10 transition-colors">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-display text-xl font-bold text-white mb-3 tracking-tight leading-tight">{title}</h3>
      <p className="text-[14px] leading-relaxed text-zinc-400">{body}</p>
    </div>
  );
}

/* ---------- Lead form ---------- */
function LeadForm() {
  const [form, setForm] = useState({
    name: "", email: "", contact_number: "", website: "",
    organisation: "", description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const req = ["name", "email", "contact_number", "website", "organisation", "description"];
    for (const k of req) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please fill in all fields — missing ${k.replace("_", " ")}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form, { timeout: 20000 });
      setSubmitted(true);
      toast.success("Application received — check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something broke. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-10 text-center" data-testid="lead-form-success">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF3B30] mb-3">// application received</div>
        <h3 className="font-display text-2xl font-bold text-white mb-3">You're on the list.</h3>
        <p className="text-zinc-400 text-[14px] leading-relaxed max-w-md mx-auto">
          One of the team will review your application and get back to you in 24-48 hours. Check your inbox for confirmation.
        </p>
      </div>
    );
  }

  const field = "w-full bg-[#050505] border border-white/10 focus:border-[#FF3B30] outline-none px-4 py-3 text-[14px] text-white placeholder:text-zinc-600 transition-colors";
  const label = "block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2";

  return (
    <form onSubmit={submit} className="border border-white/10 bg-[#0a0a0c] p-6 sm:p-10 space-y-5" data-testid="lead-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={label}>Name *</label>
          <input required value={form.name} onChange={update("name")} data-testid="lead-name" className={field} placeholder="Your full name" />
        </div>
        <div>
          <label className={label}>Email *</label>
          <input required type="email" value={form.email} onChange={update("email")} data-testid="lead-email" className={field} placeholder="you@company.com" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={label}>Contact number *</label>
          <input required value={form.contact_number} onChange={update("contact_number")} data-testid="lead-contact" className={field} placeholder="+91 98xxxxxxxx" />
        </div>
        <div>
          <label className={label}>Organisation *</label>
          <input required value={form.organisation} onChange={update("organisation")} data-testid="lead-org" className={field} placeholder="Acme Labs Pvt Ltd" />
        </div>
      </div>
      <div>
        <label className={label}>Website *</label>
        <input required value={form.website} onChange={update("website")} data-testid="lead-website" className={field} placeholder="https://…" />
      </div>
      <div>
        <label className={label}>Description / service needed *</label>
        <textarea
          required
          value={form.description}
          onChange={update("description")}
          data-testid="lead-description"
          rows={5}
          className={`${field} resize-none leading-relaxed`}
          placeholder="What are you trying to grow? Which subreddits matter? What have you tried before?"
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid="lead-submit"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-mono text-[12px] uppercase tracking-[0.2em] px-8 py-4 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        {submitting ? "Submitting…" : "Book Your Strategy Call"}
      </button>
      <p className="text-[11px] text-zinc-600 font-mono uppercase tracking-widest pt-2 border-t border-white/5">
        // We take a limited number of clients per quarter. Not every application is accepted.
      </p>
    </form>
  );
}

/* ---------- Page ---------- */
export default function Landing() {
  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#FF3B30] selection:text-white">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#050505]/70 border-b border-white/5" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 h-14">
          <div className="flex items-center gap-2 font-display font-black tracking-tight text-lg" data-testid="landing-logo">
            <span className="text-white">THREADOPS</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30] border border-[#FF3B30]/40 px-1.5 py-0.5">crm</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="#apply" data-testid="landing-nav-apply" className="hidden sm:inline-flex bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 transition-colors">
              Apply for a campaign
            </a>
            <a href="/login" data-testid="landing-nav-login" className="text-zinc-400 hover:text-white font-mono text-[11px] uppercase tracking-[0.2em]">
              Sign in
            </a>
          </div>
        </div>
      </nav>

      {/* ================ HERO ================ */}
      <section className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden" data-testid="landing-hero">
        <NetworkCanvas />
        <div className="absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-[#FF3B30]/5 to-transparent pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <Eyebrow>// Reddit growth · done for you · India-native operators</Eyebrow>
          <H1>
            Dominate Reddit. <br className="hidden sm:block" />
            <span className="text-[#FF3B30]">Leave zero footprints.</span>
          </H1>
          <p className="mt-8 text-[16px] sm:text-[18px] text-zinc-300 leading-relaxed max-w-2xl">
            We orchestrate organic conversations around your brand using a distributed
            network of real, aged human accounts. Capture high-intent traffic, rank in
            search engines, and dominate AI LLM overviews. <span className="text-white font-semibold">No bots. No shadowbans. Pure execution.</span>
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <a
              href="#apply"
              data-testid="hero-cta-apply"
              className="inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-mono text-[12px] uppercase tracking-[0.2em] px-8 py-4 transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
              Apply for a campaign
            </a>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Accepting Q1 2026 · 3 spots left
            </div>
          </div>
        </div>
      </section>

      {/* ================ THE ENEMY ================ */}
      <section className="relative py-24 sm:py-32 border-t border-white/5" style={{ background: "radial-gradient(ellipse at top, #0a0a0c 0%, #050505 60%)" }} data-testid="landing-agitation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10 items-center min-w-0">
          <div className="lg:col-span-5 min-w-0">
            <Eyebrow color="#FF3B30">// the enemy</Eyebrow>
            <H2>The platform destroys corporate spam.</H2>
          </div>
          <div className="lg:col-span-7 min-w-0 space-y-6">
            <p className="text-[17px] text-zinc-300 leading-relaxed">
              Standard agencies fail on Reddit. The community <span className="text-[#FF3B30] font-semibold">hates blatant advertising</span>. Algorithms hunt and neutralize automated bots. One wrong move ruins your brand's reputation permanently.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 pt-4">
              {[
                { icon: ShieldOff, label: "Bot detection", body: "Shadowbans within hours" },
                { icon: Users, label: "Community backlash", body: "Screenshot → viral → dead" },
                { icon: Zap, label: "Algorithm downranks", body: "Comment karma tanks fast" },
              ].map((item, i) => (
                <div key={i} className="border border-white/10 p-4 min-w-0">
                  <item.icon className="w-4 h-4 text-[#FF3B30] mb-3" />
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{item.label}</div>
                  <div className="text-[13px] text-zinc-300">{item.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================ THE SOLUTION ================ */}
      <section className="relative py-24 sm:py-32 border-t border-white/5" data-testid="landing-solution">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <Eyebrow>// our methodology</Eyebrow>
            <H2>Ghost execution. <span className="text-[#FF3B30]">Real results.</span></H2>
            <p className="mt-8 text-[17px] text-zinc-300 leading-relaxed">
              We bypass the algorithms completely. Our team deploys a distributed network of real, aged accounts across the globe. We seed your product into hyper-targeted subreddits <span className="text-white font-semibold">exactly when</span> users ask for solutions. We generate the demand; you collect the revenue.
            </p>
          </div>
        </div>
      </section>

      {/* ================ CONSENSUS ENGINEERING ================ */}
      <section className="relative py-24 sm:py-32 border-t border-white/5 bg-[#08080a]" data-testid="landing-consensus">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-16">
            <Eyebrow color="#FF3B30">// consensus engineering</Eyebrow>
            <H2>Three pillars.<br />Invisible → inevitable.</H2>
            <p className="mt-6 text-[15px] text-zinc-400 leading-relaxed max-w-xl">
              Three strategic vectors that move your brand from unknown to default — at every moment a buyer decides.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4 min-w-0">
            <Pillar
              num="01"
              kicker="disrupt"
              title="Competitor intercepts"
              body="We intercept active comparisons the moment buyers weigh your rivals. Karma-aged personas enter organically and shift the verdict — naturally, credibly."
              bullets={[
                "Real-time competitor monitoring",
                "Comment ladder strategy",
                "Myth-busting threads",
                "Decision-moment targeting",
              ]}
            />
            <Pillar
              num="02"
              kicker="capture"
              title="Opportunity capture"
              body="Identify buyers asking for exactly what you solve. Deploy peer-first recommendations that guide them to your offer — no ads, no detection."
              bullets={[
                "Subreddit intent mapping",
                "Problem-solution seeding",
                "AMA facilitation",
                "Negative seeding → engagement amplifier",
              ]}
            />
            <Pillar
              num="03"
              kicker="create"
              title="AI answer seeding™"
              body="We engineer threads that feed ChatGPT, Gemini, and Perplexity. Optimised llms.txt, citation architecture, and structured data so AI recommends you by default."
              bullets={[
                "llms.txt + schema optimisation",
                "Citation trigger embedding",
                "LLM referral tracking (GA4)",
                "Agenda priming for launches",
              ]}
            />
          </div>
        </div>
      </section>

      {/* ================ CORE OFFERINGS ================ */}
      <section className="relative py-24 sm:py-32 border-t border-white/5" data-testid="landing-offerings">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-16">
            <Eyebrow>// what we deploy</Eyebrow>
            <H2>Core offerings.</H2>
          </div>
          <div className="grid md:grid-cols-3 gap-4 min-w-0">
            <OfferingCard
              num="/ 01"
              icon={Target}
              title="Tactical link placements."
              body="We identify high-converting threads and drop your URL naturally into the discussion — placed by humans who look like they belong there."
            />
            <OfferingCard
              num="/ 02"
              icon={TrendingUp}
              title="Organic upvote swarms."
              body="We control the narrative. Push positive mentions to the top of the feed and bury competitor spam under authentic-looking community backlash."
            />
            <OfferingCard
              num="/ 03"
              icon={Cpu}
              title="AI & LLM optimisation."
              body="Deep Reddit threads train modern AI search overviews (Gemini, ChatGPT, Perplexity). We embed your brand directly into that training data."
            />
          </div>
        </div>
      </section>

      {/* ================ FINAL CTA + FORM ================ */}
      <section id="apply" className="relative py-24 sm:py-32 border-t border-white/5 bg-[#08080a]" data-testid="landing-apply">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <Eyebrow>// limited quarterly slots</Eyebrow>
            <H2>Stop guessing. <span className="text-[#FF3B30]">Start scaling.</span></H2>
            <p className="mt-6 text-[16px] text-zinc-400 leading-relaxed max-w-xl mx-auto">
              We accept a limited number of clients per quarter to maintain strict operational security. Apply below — we reply within 48 hours.
            </p>
          </div>
          <LeadForm />
        </div>
      </section>

      {/* ================ FOOTER ================ */}
      <footer className="border-t border-white/5 py-10" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            © ThreadOps CRM · Command Center
          </div>
          <div className="flex items-center gap-6">
            <a href="/login" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white">Sign in</a>
            <a href="#apply" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white">Apply</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
